import { scaleBand, scaleLinear } from "d3";
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager";
import { MarginedBoundingBox } from "../../../../Types/MarginedBoundingBox";
import { Offset } from "../../../../Types/Offset";
import { ReactCallbacks } from "../../../../Types/ReactCallbacks";
import { LEFT_MARGIN } from "../../Constants";
import { D3TimeBasedVisualization } from "../../D3TimeBasedVisualization";
import { range } from "lodash";
import { ModalityDataSource } from "../../../../Types/ModalityDataSource";
import { ModalityPage } from "../../../../Data/ModalityPage";
import { DataSource } from "../../../../Types/DataSource";
import { TraceDataConfig } from "../../../../Types/Trace";
import { Analytic, COxAnalysis, MAPOptAnalysis, MAPOptFlexAnalysis } from "../../../../Types/AnalysisDetails";
import { MAPOptData, MAPOptPlotConfig } from "../../../../Types/MAPOptPlot";
import { D3MAPOptPlotConfigurationBuilder } from "./D3MAPOptConfigurationBuilder";
import { D3MAPOptPlotRenderer } from "./D3MAPOptRenderer";

export class D3MAPOptPlot extends D3TimeBasedVisualization<MAPOptPlotConfig, ReactCallbacks<any>, D3MAPOptPlotRenderer, TimeSeriesPageManager<ModalityPage>> {
    public xScaleBand = scaleBand()
    public xScaleLinear = scaleLinear()
    public mapoptBoxPlotYScale = scaleLinear().domain([20, 120]).clamp(true)
    public coxBoxPlotYScale = scaleLinear().domain([-1, 1]).clamp(true)
    public yScale = scaleLinear().domain([-1, 1])
    public pageManager: TimeSeriesPageManager<any>
    public bins: number[] = []
    public curveFitBoundingBox: MarginedBoundingBox
    public summaryValuesBoundingBox: MarginedBoundingBox
    public mapoptBoxPlotBoundingBox: MarginedBoundingBox
    public coxBoxPlotBoundingBox: MarginedBoundingBox
    private curveFitMargins: Offset = { top: 65, left: LEFT_MARGIN, bottom: 132, right: 0 }

    public mapoptData: MAPOptData = {
        MAPOpt: null,
        LLA: null,
        ULA: null,
        includedDataPercentage: null,
        fitType: null,
        fitSpan: null,
        yValues: new Array(this.bins.length).fill(null),
        errors: new Array(this.bins.length).fill(null),
        includedInFit: new Array(this.bins.length).fill(null),
        dataPercentages: new Array(this.bins.length).fill(null),
        coefficients: [null, null, null],
        weight: null,
        startTime: null,
        endTime: null,
    }

    constructor(root: HTMLDivElement, config: MAPOptPlotConfig, pageManager: TimeSeriesPageManager<any>, reactCallbacks: ReactCallbacks<any>) {
        super(root, config, pageManager, reactCallbacks)
        this.pageManager = pageManager
        this.bins = range(21, 121, 2)

        this.curveFitBoundingBox = new MarginedBoundingBox(
            { width: 3*config.dimensions.width/8, height: config.dimensions.height }, 
            this.curveFitMargins
        )

        this.mapoptBoxPlotBoundingBox = new MarginedBoundingBox(
            { width: config.dimensions.width/4, height: config.dimensions.height },
            { top: 10, left: 40, bottom: 40, right: 10 }
        )

        this.coxBoxPlotBoundingBox = new MarginedBoundingBox(
            { width: config.dimensions.width/4, height: config.dimensions.height },
            { top: 10, left: 40, bottom: 40, right: 10 }
        )
        
        this.summaryValuesBoundingBox = new MarginedBoundingBox(
            { width: config.dimensions.width/2, height: config.dimensions.height },
            { top: 65, left: 10, bottom: 40, right: 10 }
        )

        this.mount(new D3MAPOptPlotRenderer(this, new D3MAPOptPlotConfigurationBuilder(this), "d3-mapopt-plot"))
    }

    public getVisibleTraces(): TraceDataConfig[] {
        return []
    }

    public getMAPoptAnalysis(): MAPOptAnalysis | MAPOptFlexAnalysis {
        return {
            analytic: this.config.mapoptAlgorithm,
            ABP: this.config.abpModality,
            stO2Left: this.config.stO2Left,
            stO2Right: this.config.stO2Right,
            enableLeft: this.config.enableLeft,
            enableRight: this.config.enableRight,
            calculationWindowMs: this.config.mapoptAlgorithm === Analytic.MAPOpt ? 4 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000,
            calculationPeriodMs: 60 * 1000,
            coxThreshold: this.config.coxThreshold
        }
    }

    public getCOxAnalysis(): COxAnalysis {
        return {
            analytic: Analytic.COx,
            ABP: this.config.abpModality,
            StO2Left: this.config.stO2Left,
            StO2Right: this.config.stO2Right,
            enableLeft: this.config.enableLeft,
            enableRight: this.config.enableRight,
            calculationWindowMs: 5 * 60 * 1000,
            calculationPeriodMs: 60 * 1000,
        }
    }

    public getModalityDataSources(): ModalityDataSource[] {
        const dataObjectId = this.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number

        return [
            {
                modality: "MAPOpt,Composite,SampleSeries",
                dataObjectId,
                onDemandAnalysis: this.getMAPoptAnalysis()
            },
            {
                modality: "COx",
                dataObjectId,
                onDemandAnalysis: this.getCOxAnalysis()
            }
        ]
    }

    public onYAxisDragStart = () => {
        this.renderer?.onYAxisDragStart()
    }

    public onYAxisDrag = () => {
        this.renderer?.onYAxisDrag()
    }

    public onYAxisDragEnd = () => {
        this.renderer?.onYAxisDragEnd()
    }

    public autoScale = () => {
        this.yScale.domain([-1, 1])
        this.renderer?.render()
    }
    
    public getXScaleLabels() {
        return this.bins.slice(0, -1).map((bin, index) => `${bin}-${this.bins[index + 1]}`)
    }
    
    protected renderPage(page: ModalityPage): void {
        this.renderer?.renderPage(page)
    }

    protected updateDerivedState(): void {
        const timelineHeight = 70
        const titleSpace = 20
        this.curveFitBoundingBox.setDimensions({ width: 3*this.config.dimensions.width/8, height: this.config.dimensions.height })
        this.summaryValuesBoundingBox.setDimensions({ width: this.config.dimensions.width/2, height: 3*this.config.dimensions.height/4 - timelineHeight - titleSpace})
        this.mapoptBoxPlotBoundingBox.setDimensions({ width: this.config.dimensions.width/4, height: 3*this.config.dimensions.height/4 - timelineHeight - titleSpace })
        this.coxBoxPlotBoundingBox.setDimensions({ width: this.config.dimensions.width/4, height: 3*this.config.dimensions.height/4 - timelineHeight - titleSpace })
        // The view scale controls the number of points that we render. 
        // So, setting a minimum of 1440 points here, which is full resolution for a modality sampled once per minute at a page duration of 1 day. 
        this.config.viewScale.range([0, Math.max(this.curveFitBoundingBox.width, 1440)])

        this.yScale.range([this.curveFitBoundingBox.height, 0])

        this.xScaleBand
            .domain(this.getXScaleLabels())
            .range([0, this.curveFitBoundingBox.width])

        const firstBin = this.bins[0]
        const lastBin = this.bins[this.bins.length - 1]

        this.xScaleLinear
            .domain([firstBin, lastBin])
            .range([0, this.curveFitBoundingBox.width])

        this.mapoptBoxPlotYScale.range([this.mapoptBoxPlotBoundingBox.height, 0])
        this.coxBoxPlotYScale.range([this.coxBoxPlotBoundingBox.height, 0])

        this.renderer?.updateChildren()
    }
}